// Sugalarm — Cycle 451: THE CHROME INVARIANT, as a rule in code.
//
// The app chrome is the header and the nav (the kiosk `KioskHeader` +
// `EdgeRail`/`FooterTabs`; the phone `MobileHeader` + `TabBar`). It is on
// by default, on every screen, in every state. The set of surfaces that
// may take it away is CLOSED, and it is this file.
//
// ⚠ WHY A FILE EXISTS FOR THREE BOOLEANS. Cycle 145 suppressed the chrome
// for the bedside clock face by reusing the `/login` seam, and derived
// "the face is on screen" as `clockMode.active && pathname === "/now"`.
// That was true until cycle 407 put the TREATING SCREEN in front of the
// face as an early return in `NowScreen` — from then on, a caregiver who
// declared "I'm treating this" during a scheduled bedside window got a
// treating screen with NO header, NO rail and a band of wallpaper along
// the bottom, on the kiosk AND on the phone. The suppression was never
// decided for that surface; it was inherited by a predicate that had
// stopped being true. A named allowlist makes inheriting it impossible:
// a surface is suppressed because it is ON THIS LIST, not because some
// other surface's proxy happened to still read true.
//
// ⚠ NOTHING ABOUT AN ALARM BELONGS IN THIS FILE. Treating, custody, a
// quiet window, a pending check-in, an episode phase — none of them are
// a reason to hide the chrome, and a term for any of them here is the
// defect above, restored. `treatingChrome.test.ts` fails the build if one
// appears. The deliberate full-bleed ALARM surfaces (the urgent takeover,
// the clock-mode alarm takeover, the preview overlay, the on-screen
// keyboard, modals and sheets) do not use this mechanism at all: they
// paint OVER the chrome by z-index and leave the frame alone.

/**
 * Every surface that may suppress the app chrome. Adding one is a
 * deliberate edit here AND in the guard's pinned copy — which is the
 * point of the list.
 *
 *   login             — the sign-in screen owns the viewport; there is no
 *                       member context to put in a header (cycle 9).
 *   clockFace         — the bedside clock FACE is the surface on screen
 *                       (cycle 145). ⚠ The face, not "clock mode is on":
 *                       a Now surface that replaces the face takes the
 *                       suppression with it. See `App.tsx`.
 *   pairingTakeover   — the Android-tablet relink `/pair` takeover fills
 *                       the whole 1024×640 stage (cycle 220).
 */
export const CHROME_SUPPRESSING_SURFACES = [
  "login",
  "clockFace",
  "pairingTakeover",
] as const;

export type ChromeSuppressingSurface =
  (typeof CHROME_SUPPRESSING_SURFACES)[number];

/** Which of the allowed surfaces is on screen. Every field is a fact
 *  about a SURFACE, never about an alarm's state. */
export type ChromeSurfaceFlags = Record<ChromeSuppressingSurface, boolean>;

/**
 * The one suppressing surface on screen, or null when the chrome stays.
 * Returns the name rather than a bare boolean so a caller (and a test)
 * can say WHICH surface took the chrome away.
 */
export function chromeSuppressedBy(
  flags: ChromeSurfaceFlags,
): ChromeSuppressingSurface | null {
  for (const surface of CHROME_SUPPRESSING_SURFACES) {
    if (flags[surface]) return surface;
  }
  return null;
}

/** The value `KioskFrame`'s `showChrome` prop takes: chrome ON unless one
 *  of the allowed surfaces is up. */
export function showAppChrome(flags: ChromeSurfaceFlags): boolean {
  return chromeSuppressedBy(flags) == null;
}
